import React, {
  Fragment,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import PageTitle2 from "../../components/pagetitle/PageTitle2";
import Header from "../../components/header/Header";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import { FaSearch, FaTimes } from "react-icons/fa";
import { API_URL } from "../../constant";

function ActiveRetailersPage(props) {
  // Retailers state
  const [retailers, setRetailers] = useState([]);
  const [filteredRetailers, setFilteredRetailers] = useState([]);
  const [isLoadingRetailers, setIsLoadingRetailers] = useState(true);
  const [retailersError, setRetailersError] = useState(null);

  // Filters state
  const [filters, setFilters] = useState({
    search: "",
    category: "all",
  });

  // Selected retailer for detailed view
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Lock body scroll when mobile modal is open
  useEffect(() => {
    if (selectedRetailer && isMobile) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
    };
  }, [selectedRetailer, isMobile]);

  // Available categories
  const [categories, setCategories] = useState(["all"]);
  const searchInputRef = useRef(null);

  // Function to fetch retailers from API
  const fetchRetailers = useCallback(async () => {
    try {
      setIsLoadingRetailers(true);
      setRetailersError(null);

      // Build query parameters
      const queryParams = new URLSearchParams();

      if (filters.search.trim()) {
        queryParams.append("search", filters.search.trim());
      }

      const url = `${API_URL}/api/public/retailers/directory?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const retailersData = data.data.directory || [];
        setRetailers(retailersData);
        applyFilters(retailersData);

        // Extract unique retail types for categories
        const types = [
          "all",
          ...new Set(
            retailersData
              .map((r) => r.business_info?.retail_type)
              .filter(Boolean)
          ),
        ];
        setCategories(types);
      } else {
        throw new Error(data.message || "Failed to load retailers");
      }
    } catch (error) {
      console.error("Failed to fetch retailers:", error);
      setRetailersError("Failed to load active retailers. Showing sample data instead.");

      // Fallback to sample data
      const fallbackData = [
        {
          user_id: 1,
          name: "TopCoinLand.Com",
          photo_url: "https://dummyimage.com/200x200/2D4CA8/FFFFFF.png&text=TC",
          business_info: {
            retail_name: "TopCoinLand.Com",
            retail_type: "Crypto Exchange",
            website: "https://topcoinland.com",
          },
        },
        {
          user_id: 2,
          name: "EilandIT.Com",
          photo_url: "https://dummyimage.com/200x200/9F4AFF/FFFFFF.png&text=EIT",
          business_info: {
            retail_name: "EilandIT.Com",
            retail_type: "Technology Retailer",
            website: "https://eilandit.com",
          },
        },
        {
          user_id: 3,
          name: "Imaxigreen.com",
          photo_url: "https://dummyimage.com/200x200/FF6B6B/FFFFFF.png&text=IG",
          business_info: {
            retail_name: "Imaxigreen.com",
            retail_type: "Green Technology",
            website: "https://imaxigreen.com",
          },
        },
        {
          user_id: 4,
          name: "Arora Hotel",
          photo_url: "https://dummyimage.com/200x200/4CAF50/FFFFFF.png&text=AH",
          business_info: {
            retail_name: "Arora Hotel",
            retail_type: "Hospitality",
            website: "https://arorahotel.com",
          },
        },
        {
          user_id: 5,
          name: "BestOne.Com",
          photo_url: "https://dummyimage.com/200x200/FF9800/FFFFFF.png&text=BO",
          business_info: {
            retail_name: "BestOne.Com",
            retail_type: "E-commerce",
            website: "https://bestone.com",
          },
        },
        {
          user_id: 6,
          name: "Eilandon.com",
          photo_url: "https://dummyimage.com/200x200/9C27B0/FFFFFF.png&text=ED",
          business_info: {
            retail_name: "Eilandon.com",
            retail_type: "Online Retailer",
            website: "https://eilandon.com",
          },
        },
      ];

      setRetailers(fallbackData);
      applyFilters(fallbackData);
      setCategories([
        "all",
        "Crypto Exchange",
        "Technology Retailer",
        "Green Technology",
        "Hospitality",
        "E-commerce",
        "Online Retailer",
      ]);
    } finally {
      setIsLoadingRetailers(false);
    }
  }, [filters]);

  // Apply filters to retailers
  const applyFilters = (data) => {
    const { search, category } = filters;
    
    const filtered = data.filter((retailer) => {
      // Search filter
      const searchLower = search.toLowerCase().trim();
      const matchesSearch = !searchLower || 
        retailer.name?.toLowerCase().includes(searchLower) ||
        retailer.business_info?.retail_name?.toLowerCase().includes(searchLower) ||
        retailer.business_info?.website?.toLowerCase().includes(searchLower);

      // Category filter
      const matchesCategory = category === "all" || 
        retailer.business_info?.retail_type === category;

      return matchesSearch && matchesCategory;
    });

    setFilteredRetailers(filtered);
  };

  // Fetch retailers on component mount and when filters change
  useEffect(() => {
    fetchRetailers();
  }, [filters]);

  // Focus search input on page load
  useEffect(() => {
    const focusTimer = setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 300);

    return () => clearTimeout(focusTimer);
  }, []);

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      search: "",
      category: "all",
    });
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
      searchInputRef.current.focus();
    }
  };

  // Get fallback initials for retailers without photos
  const getFallbackInitials = (retailer) => {
    const businessName = retailer.business_info?.retail_name || retailer.name;
    return businessName
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <Fragment>
      <div className="index_ico page_wrapper">
        <Header />
        <main className="page_content">
          <PageTitle2 pageTitle={"Active Retailers"} pagesub={"Home"} />

          {/* Main Retailers Section - Modified for full width on desktop */}
          <div className="wrap" style={{ 
            width: "100%",
            maxWidth: "100%",
            margin: "130px 0 0 0", 
            padding: "64px 5% 160px",
            backgroundColor: "#ffffff",
            boxSizing: "border-box"
          }}>
            {/* Container for centering content while maintaining full width background */}
            <div style={{
              maxWidth: "1200px",
              margin: "0 auto",
              width: "100%"
            }}>
              <header style={{ display: "grid", gap: "14px", marginBottom: "18px" }}>
                <h2 style={{ 
                  fontSize: "2.2rem",
                  margin: 0, 
                  letterSpacing: "-0.02em",
                  color: "#12141b"
                }}>
                  Active Retailers
                </h2>
                
                <p style={{ 
                  margin: 0, 
                  color: "#5b6478", 
                  maxWidth: "75ch",
                  fontSize: "1.1rem",
                  lineHeight: 1.5
                }}>
                  {/* A compact directory of the brands we work with. Search or filter to find a retailer quickly. */}
                  A directory of independent businesses participating in the TopCoin UK advertising and loyalty programme. 
                  Search or filter to find a retailer quickly.
                </p>

               {/* Search and Filter Controls */}
                <div className="controls" style={{
                  marginTop: "10px",
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "10px",
                  padding: "14px",
                  border: "1px solid rgba(0,0,0,.10)",
                  background: "#ffffff",
                  borderRadius: "14px",
                  boxShadow: "0 10px 30px rgba(0,0,0,.08)"
                }}>
                  {/* Search Field */}
                  <div className="field" style={{ display: "grid", gap: "6px" }}>
                    <label htmlFor="search" style={{ fontSize: "13px", color: "#5b6478" }}>
                      Search retailers
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        id="search"
                        type="search"
                        ref={searchInputRef}
                        placeholder="Type a name or website…"
                        value={filters.search}
                        onChange={(e) => handleFilterChange("search", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "12px 12px 12px 40px",
                          borderRadius: "12px",
                          border: "1px solid rgba(0,0,0,.10)",
                          background: "#ffffff",
                          color: "#12141b",
                          outline: "none",
                          fontSize: "14px"
                        }}
                      />
                      <FaSearch 
                        style={{
                          position: "absolute",
                          left: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#5b6478"
                        }}
                        size={16}
                      />
                      {filters.search && (
                        <button
                          onClick={() => handleFilterChange("search", "")}
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "none",
                            border: "none",
                            color: "#5b6478",
                            cursor: "pointer",
                            padding: "4px"
                          }}
                        >
                          <FaTimes size={12} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Category Filter */}
                  <div className="field" style={{ display: "grid", gap: "6px" }}>
                    <label htmlFor="category" style={{ fontSize: "13px", color: "#5b6478" }}>
                      Category
                    </label>
                    <select
                      id="category"
                      value={filters.category}
                      onChange={(e) => handleFilterChange("category", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "12px 12px",
                        borderRadius: "12px",
                        border: "1px solid rgba(0,0,0,.10)",
                        background: "#ffffff",
                        color: "#12141b",
                        outline: "none",
                        fontSize: "14px",
                        cursor: "pointer"
                      }}
                    >
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category === "all" ? "All" : category}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Reset Button */}
                  <div className="field" style={{ alignSelf: "end" }}>
                    <button
                      onClick={handleResetFilters}
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        borderRadius: "12px",
                        border: "1px solid rgba(0,0,0,.10)",
                        background: "#ffffff",
                        color: "#12141b",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        fontSize: "14px"
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = "rgba(0,0,0,.05)";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = "#ffffff";
                      }}
                    >
                      Reset
                    </button>
                  </div>

                  {/* Results Count */}
                  <div className="meta" style={{
                    gridColumn: "1 / -1",
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    marginTop: "8px",
                    color: "#5b6478",
                    fontSize: "13px"
                  }}>
                    <span>
                      <strong>{filteredRetailers.length}</strong> shown
                    </span>
                  </div>
                </div>
              </header>

              {/* Loading State */}
              {isLoadingRetailers && (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <div style={{ 
                    width: "40px", 
                    height: "40px", 
                    border: "3px solid rgba(0,0,0,.10)",
                    borderTopColor: "#7aa7ff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto"
                  }} />
                  <p style={{ marginTop: "12px", color: "#5b6478" }}>
                    Loading retailers...
                  </p>
                </div>
              )}

              {/* Error State */}
              {retailersError && !isLoadingRetailers && (
                <div style={{ 
                  textAlign: "center", 
                  padding: "20px",
                  backgroundColor: "rgba(255, 107, 107, 0.1)",
                  borderRadius: "12px",
                  border: "1px solid rgba(255, 107, 107, 0.2)",
                  margin: "20px 0"
                }}>
                  <p style={{ color: "#ff6b6b", marginBottom: "8px" }}>
                    {retailersError}
                  </p>
                  <p style={{ color: "#5b6478", fontSize: "0.9rem" }}>
                    Showing sample data. Please check your connection and try again.
                  </p>
                </div>
              )}

              {/* No Results State */}
              {!isLoadingRetailers && !retailersError && filteredRetailers.length === 0 && (
                <div style={{
                  marginTop: "18px",
                  padding: "18px",
                  borderRadius: "14px",
                  border: "1px dashed rgba(0,0,0,.10)",
                  color: "#5b6478",
                  textAlign: "center"
                }}>
                  No retailers match your search/filter.
                </div>
              )}

              {/* Retailers Grid */}
              {!isLoadingRetailers && filteredRetailers.length > 0 && selectedRetailer === null && (
                <section 
                  className="grid" 
                  style={{
                    marginTop: "18px",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: "12px",
                    width: "100%"
                  }}
                  aria-label="Retailer listings"
                >
                  {filteredRetailers.map((retailer) => {
                    const websiteUrl = retailer.business_info?.website;
                    const displayName = retailer.business_info?.retail_name || retailer.name;
                    const displayUrl = websiteUrl?.replace(/^https?:\/\//, "");
                    
                    return (
                      <article 
                        key={retailer.user_id} 
                        className="card"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "52px 1fr",
                          gap: "10px",
                          padding: "12px",
                          border: "1px solid rgba(0,0,0,.10)",
                          borderRadius: "14px",
                          background: "#ffffff",
                          boxShadow: "0 6px 18px rgba(0,0,0,.08)",
                          alignItems: "center",
                          minHeight: "78px",
                          transition: "all 0.2s ease"
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.borderColor = "#7aa7ff";
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,.12)";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.borderColor = "rgba(0,0,0,.10)";
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,.08)";
                        }}
                      >
                        {websiteUrl ? (
                          <a 
                            href={websiteUrl} 
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedRetailer(retailer);
                            }}
                            style={{
                              textDecoration: "none",
                              color: "inherit",
                              display: "contents",
                              cursor: "pointer"
                            }}
                          >
                            <div style={{
                              width: "52px",
                              height: "52px",
                              borderRadius: "14px",
                              border: "1px solid rgba(0,0,0,.10)",
                              background: "#ffffff",
                              display: "grid",
                              placeItems: "center",
                              overflow: "hidden"
                            }}>
                              <img
                                src={retailer.photo_url}
                                alt={`${displayName} logo`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                  padding: "6px",
                                  display: "block"
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  const fallbackSpan = document.createElement("span");
                                  fallbackSpan.className = "fallback";
                                  fallbackSpan.textContent = getFallbackInitials(retailer);
                                  fallbackSpan.style.fontWeight = "700";
                                  fallbackSpan.style.color = "#5b6478";
                                  fallbackSpan.style.fontSize = "14px";
                                  fallbackSpan.style.letterSpacing = "0.02em";
                                  e.target.parentElement.appendChild(fallbackSpan);
                                }}
                              />
                            </div>
                            <div>
                              <h3 style={{
                                margin: "0",
                                fontSize: "15px",
                                letterSpacing: "-0.01em",
                                color: "#12141b"
                              }}>
                                {displayName}
                              </h3>
                              <p style={{
                                margin: "3px 0 0",
                                fontSize: "13px",
                                color: "#5b6478",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                              }}>
                                {displayUrl}
                              </p>
                            </div>
                          </a>
                        ) : (
                          <>
                            <div style={{
                              width: "52px",
                              height: "52px",
                              borderRadius: "14px",
                              border: "1px solid rgba(0,0,0,.10)",
                              background: "#ffffff",
                              display: "grid",
                              placeItems: "center",
                              overflow: "hidden"
                            }}>
                              <span style={{
                                fontWeight: "700",
                                color: "#5b6478",
                                fontSize: "14px",
                                letterSpacing: "0.02em"
                              }}>
                                {getFallbackInitials(retailer)}
                              </span>
                            </div>
                            <div>
                              <h3 style={{
                                margin: "0",
                                fontSize: "15px",
                                letterSpacing: "-0.01em",
                                color: "#12141b"
                              }}>
                                {displayName}
                              </h3>
                              {retailer.business_info?.retail_type && (
                                <p style={{
                                  margin: "3px 0 0",
                                  fontSize: "13px",
                                  color: "#5b6478",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap"
                                }}>
                                  {retailer.business_info.retail_type}
                                </p>
                              )}
                            </div>
                          </>
                        )}
                      </article>
                    );
                  })}
                </section>
              )}

              {/* Desktop inline view */}
              {selectedRetailer !== null && !isMobile && (
                <div style={{ display: "flex", gap: "20px", marginTop: "18px", width: "100%", alignItems: "flex-start" }}>
                  <div style={{ flex: "1", maxWidth: "400px" }}>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "52px 1fr",
                      gap: "10px",
                      padding: "12px",
                      border: "1px solid rgba(0,0,0,.10)",
                      borderRadius: "14px",
                      background: "#ffffff",
                      boxShadow: "0 6px 18px rgba(0,0,0,.08)",
                      alignItems: "center",
                      minHeight: "78px",
                    }}>
                      <div style={{
                        width: "52px",
                        height: "52px",
                        borderRadius: "14px",
                        border: "1px solid rgba(0,0,0,.10)",
                        background: "#ffffff",
                        display: "grid",
                        placeItems: "center",
                        overflow: "hidden"
                      }}>
                        <img
                          src={selectedRetailer.photo_url}
                          alt={`${selectedRetailer.business_info?.retail_name || selectedRetailer.name} logo`}
                          style={{ width: "100%", height: "100%", objectFit: "contain", padding: "6px" }}
                          onError={(e) => {
                            e.target.style.display = "none";
                            const fallbackSpan = document.createElement("span");
                            fallbackSpan.textContent = getFallbackInitials(selectedRetailer);
                            fallbackSpan.style.fontWeight = "700";
                            fallbackSpan.style.color = "#5b6478";
                            fallbackSpan.style.fontSize = "14px";
                            fallbackSpan.style.letterSpacing = "0.02em";
                            e.target.parentElement.appendChild(fallbackSpan);
                          }}
                        />
                      </div>
                      <div>
                        <h3 style={{ margin: "0", fontSize: "15px", letterSpacing: "-0.01em", color: "#12141b" }}>
                          {selectedRetailer.business_info?.retail_name || selectedRetailer.name}
                        </h3>
                        <p style={{ margin: "3px 0 0", fontSize: "13px", color: "#5b6478", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {selectedRetailer.business_info?.retail_type}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ flex: "2", height: "600px", padding: "20px", border: "1px solid rgba(0,0,0,.10)", borderRadius: "14px", background: "#ffffff", boxShadow: "0 6px 18px rgba(0,0,0,.08)", display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                      <h3 style={{ margin: 0, fontSize: "18px", color: "#12141b" }}>Retailer Website</h3>
                      <button 
                        onClick={() => setSelectedRetailer(null)}
                        style={{ background: "none", border: "none", cursor: "pointer", textDecoration: "underline", fontWeight: "bold", fontSize: "16px", color: "#12141b" }}
                      >
                        Back
                      </button>
                    </div>
                    <div style={{ flex: 1, position: "relative", borderRadius: "8px", overflow: "hidden", border: "1px solid rgba(0,0,0,.05)" }}>
                      <iframe 
                        src={selectedRetailer.business_info?.website} 
                        style={{ width: "100%", height: "100%", border: "none", position: "absolute", top: 0, left: 0 }}
                        title="Retailer Website"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Website Modal */}
          {selectedRetailer && isMobile && (
            <div 
              className="modal-overlay" 
              onClick={() => setSelectedRetailer(null)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "rgba(0,0,0,0.6)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
                touchAction: "none"
              }}
            >
              <div 
                className="modal-content" 
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "#fff",
                  width: "90%",
                  maxWidth: "1200px",
                  height: "85vh",
                  borderRadius: "12px",
                  // overflow: "hidden",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                <div style={{ 
                  padding: "12px 16px", 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center", 
                  background: "#f8f9fa", 
                  borderBottom: "1px solid rgba(0,0,0,.10)",
                  flexShrink: 0
                }}>
                  <h3 style={{ margin: 0, fontSize: "16px", color: "#12141b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, marginRight: "10px" }}>
                    {selectedRetailer.business_info?.retail_name || selectedRetailer.name}
                  </h3>
                  <button 
                    onClick={() => setSelectedRetailer(null)}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "16px",
                      cursor: "pointer",
                      color: "#12141b",
                      textDecoration: "underline",
                      fontWeight: "bold",
                      flexShrink: 0,
                      padding: "8px"
                    }}
                  >
                    Back
                  </button>
                </div>
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <iframe 
                    src={selectedRetailer.business_info?.website} 
                    style={{ 
                      width: "100%", 
                      height: "100%", 
                      border: "none",
                      display: "block"
                    }}
                    title="Retailer Website"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                  />
                </div>
              </div>
            </div>
          )}
        </main>
        <Footer />
        <Scrollbar />
      </div>

      <style>
        {`
          /* Modern Design Variables - Light Theme */
          :root {
            --bg: #ffffff;
            --panel: #ffffff;
            --panel2: #ffffff;
            --text: #12141b;
            --muted: #5b6478;
            --border: rgba(0,0,0,.10);
            --focus: #7aa7ff;
            --radius: 14px;
            --shadow: 0 10px 30px rgba(0,0,0,.08);
            --cardW: 220px;
            --hover-bg: rgba(0,0,0,.05);
          }

          /* Base styles */
          body {
            background: #ffffff;
            color: var(--text);
            margin: 0;
            font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
          }

          /* Focus styles */
          input[type="search"]:focus, 
          select:focus, 
          button:focus,
          a:focus {
            box-shadow: 0 0 0 3px rgba(122, 167, 255, 0.3);
            border-color: rgba(122, 167, 255, 0.5);
            outline: none;
          }

          /* Button hover effects */
          button:hover {
            background: rgba(0,0,0,.05) !important;
          }

          button:active {
            transform: translateY(1px);
          }

          /* Loading spinner animation */
          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          /* Responsive Design */
          @media (min-width: 680px) {
            .controls {
              grid-template-columns: 1.4fr .6fr auto !important;
            }
          }

          @media (min-width: 1024px) {
            .wrap {
              padding-left: 8% !important;
              padding-right: 8% !important;
            }
          }

          @media (min-width: 1440px) {
            .wrap {
              padding-left: 12% !important;
              padding-right: 12% !important;
            }
          }

          @media (max-width: 768px) {
            .wrap {
              padding: 20px 15px 40px !important;
            }
            
            .grid {
              grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)) !important;
            }
          }

          @media (max-width: 480px) {
            .grid {
              grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)) !important;
            }
            
            .card {
              grid-template-columns: 40px 1fr !important;
              gap: 16px !important;
              padding: 10px !important;
            }
            
            .card > div:first-child {
              width: 40px !important;
              height: 40px !important;
            }
          }

          /* Scrollbar styling */
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          ::-webkit-scrollbar-track {
            background: #f1f1f1;
          }

          ::-webkit-scrollbar-thumb {
            background: rgba(0,0,0,.2);
            border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: rgba(0,0,0,.3);
          }

          /* Improve card image rendering */
          .card img {
            transition: transform 0.3s ease;
          }

          .card:hover img {
            transform: scale(1.05);
          }

          /* Smooth transitions */
          .card, input, select, button {
            transition: all 0.2s ease;
          }
        `}
      </style>
    </Fragment>
  );
}

export default ActiveRetailersPage;